import {
  Flex,
  Text,
  useColorModeValue,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Switch,
  Stack,
  Box,
  Select,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { columnsDataEmailTemplateDetail } from 'variables/columnsData';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AddIcon, ArrowBackIcon, EmailIcon } from '@chakra-ui/icons';
import { useNotification } from 'hooks/useNotification';
import SearchTable2 from 'components/Tables/SearchTable2';
import { useHistory, useParams } from 'react-router-dom';
import Editor from 'components/Editor/EmailEditor';
import TemplateKeyInsert from 'components/TemplateKeyInsert/TemplateKeyInsert';
import { checkForbidden } from 'helpers/checkForbidden';

const TYPE_OPTIONS = [
  { id: 'EMAIL', name: 'EMAIL' },
  { id: 'ZALOZNS', name: 'ZALO ZNS' },
];


function DataTableEmailTemplateDetail() {
  const textColor = useColorModeValue('gray.700', 'white');
  const notificationActions = useNotification();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenSendTest, onOpen: onOpenSendTest, onClose: onCloseSendTest } = useDisclosure();
  const [isCreateDetail, setIsCreateDetail] = useState(true);
  const [currentDetailID, setCurrentDetailID] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [templateDetails, setTemplateDetails] = useState([]);
  const [paramTags, setParamTags] = useState([]);
  const [paramInput, setParamInput] = useState('');
  const [sendTestEmail, setSendTestEmail] = useState('');
  const [sendTestParams, setSendTestParams] = useState({});
  const history = useHistory();
  const { template_id } = useParams();
  const toast = useToast();
  const subjectInputRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const lastFocusedField = useRef(null); // 'subject' | 'body'

  const templateKeys = useMemo(() => {
    return paramTags;
  }, [paramTags]);

  const handleAddParam = () => {
    const trimmed = paramInput.trim();
    if (trimmed && !paramTags.includes(trimmed)) {
      setParamTags([...paramTags, trimmed]);
    }
    setParamInput('');
  };

  const handleParamInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddParam();
    }
  };

  const handleRemoveParam = (key) => {
    setParamTags(paramTags.filter((t) => t !== key));
  };

  // Form for template info
  const templateValidationSchema = Yup.object().shape({
    name: Yup.string().required('This field is required'),
  });
  const templateFormOptions = { resolver: yupResolver(templateValidationSchema) };
  const {
    register: registerTemplate,
    handleSubmit: handleSubmitTemplate,
    formState: formStateTemplate,
    setValue: setValueTemplate,
    watch: watchTemplate,
  } = useForm(templateFormOptions);
  const { errors: errorsTemplate, isSubmitting: isSubmittingTemplate } = formStateTemplate;

  // Form for template detail
  const detailValidationSchema = Yup.object().shape({
    subject: Yup.string().nullable(),
    email_from: Yup.string().nullable(),
  });
  const detailFormOptions = { resolver: yupResolver(detailValidationSchema) };
  const {
    register: registerDetail,
    handleSubmit: handleSubmitDetail,
    formState: formStateDetail,
    reset: resetDetail,
    setValue: setValueDetail,
    control: controlDetail,
    watch: watchDetail,
  } = useForm(detailFormOptions);
  const { errors: errorsDetail, isSubmitting: isSubmittingDetail } = formStateDetail;

  useEffect(() => {
    if (template_id) {
      fetchTemplateDetail();
    }
  }, [template_id]);

  const fetchTemplateDetail = async () => {
    setIsFetching(true);
    try {
      const res = await notificationActions.getEmailTemplateDetail(template_id);
      if (res.status === 200) {
        const data = res.data.data;
        setTemplateData(data);
        setValueTemplate('name', data.name);
        setValueTemplate('isActive', data.status === 'Active');
        setTemplateDetails(data.detail || []);
        if (data.params) {
          setParamTags(data.params.split(',').map((k) => k.trim()).filter(Boolean));
        } else {
          setParamTags([]);
        }
      }
    } catch (err) {
      console.error(err);
      toast({
        description: 'Failed to load template detail',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
    setIsFetching(false);
  };

  async function onSubmitTemplate({ name, isActive }) {
    const params = {
      name,
      status: isActive ? 'Active' : 'InActive',
      params: paramTags.join(','),
    };
    try {
      const res = await notificationActions.updateEmailTemplate(params, template_id);
      if (res.status === 200) {
        toast({
          description: 'Update Email Template success',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        fetchTemplateDetail();
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function onSubmitDetail({ type, detailName, subject, email_from, body, isActiveDetail }) {
    const params = {
      name: detailName,
      subject,
      email_from: email_from || null,
      body: body || '',
      status: isActiveDetail ? 'Active' : 'InActive',
    };

    try {
      let res;
      if (isCreateDetail) {
        params.template_id = parseInt(template_id);
        params.type = type;
        res = await notificationActions.createTemplateDetail(params);
      } else {
        res = await notificationActions.updateTemplateDetail(params, currentDetailID);
      }

      if (res.status === 200) {
        onClose();
        resetDetail();
        const message = isCreateDetail
          ? 'Create Template Detail success'
          : 'Update Template Detail success';

        toast({
          description: message,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        fetchTemplateDetail();
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function editDetail(detailID) {
    try {
      const res = await notificationActions.getTemplateDetailById(detailID);
      if (res.status === 200) {
        const data = res.data.data;
        setValueDetail('type', data.type);
        setValueDetail('detailName', data.name || '');
        setValueDetail('subject', data.subject);
        setValueDetail('email_from', data.email_from || '');
        setValueDetail('body', data.body);
        setValueDetail('isActiveDetail', data.status === 'Active');
        setCurrentDetailID(detailID);
        setIsCreateDetail(false);
        onOpen();
      }
    } catch (err) {
      console.error(err);
      toast({
        description: 'Failed to load template detail',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  const handleOpenCreateDetail = () => {
    resetDetail();
    setValueDetail('type', 'EMAIL');
    setValueDetail('isActiveDetail', true);
    setIsCreateDetail(true);
    onOpen();
  };

  async function copyDetail(detailID) {
    try {
      const res = await notificationActions.getTemplateDetailById(detailID);
      if (res.status === 200) {
        const data = res.data.data;
        resetDetail();
        setValueDetail('type', data.type);
        setValueDetail('detailName', data.name || '');
        setValueDetail('subject', data.subject);
        setValueDetail('email_from', data.email_from || '');
        setValueDetail('body', data.body);
        setValueDetail('isActiveDetail', data.status === 'Active');
        setIsCreateDetail(true);
        onOpen();
      }
    } catch (err) {
      console.error(err);
      toast({
        description: 'Failed to load template detail',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  const uppercaseExceptKeys = (text) => {
    return text.replace(/({{.*?}})|([^{]+|{)/g, (match, key, other) => {
      if (key) return key;
      return other ? other.toUpperCase() : match;
    });
  };

  const handleSubjectChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const transformed = uppercaseExceptKeys(e.target.value);
    setValueDetail('subject', transformed);
    input.value = transformed;
    setTimeout(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleOpenSendTest = () => {
    setSendTestEmail('');
    const initial = {};
    paramTags.forEach((key) => { initial[key] = ''; });
    setSendTestParams(initial);
    onOpenSendTest();
  };

  const handleSendTestEmail = async () => {
    if (!sendTestEmail.trim()) {
      toast({
        description: 'Please enter email address',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    const subject = (watchDetail('subject') || '').replace(/\{\{\s*\.(\w+)\s*\}\}/g, (match, key) => {
      return sendTestParams[key] !== undefined ? sendTestParams[key] : match;
    });
    const body = (watchDetail('body') || '').replace(/\{\{\s*\.(\w+)\s*\}\}/g, (match, key) => {
      return sendTestParams[key] !== undefined ? sendTestParams[key] : match;
    });

    const payload = {
      user_info: {
        email: sendTestEmail.trim(),
      },
      body,
      subject,
      email_from: watchDetail('email_from') || null,
      params: sendTestParams,
      type: watchDetail('type') || 'EMAIL',
    };

    setIsSendingTest(true);
    try {
      const res = await notificationActions.sendTestEmail(payload);
      if (res.status === 200) {
        toast({
          description: 'Test email sent successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        description: err?.response?.data?.meta?.message || 'Failed to send test email',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
    setIsSendingTest(false);
  };

  const insertTemplateKey = (key) => {
    const insertion = `{{ .${key} }}`;

    if (lastFocusedField.current === 'subject') {
      const input = subjectInputRef.current;
      if (!input) return;
      const value = input.value || '';
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = uppercaseExceptKeys(value.slice(0, start) + insertion + value.slice(end));
      setValueDetail('subject', newValue);
      input.value = newValue;
      const cursorPos = start + insertion.length;
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(cursorPos, cursorPos);
      }, 0);
    } else if (lastFocusedField.current === 'body') {
      if (bodyEditorRef.current) {
        bodyEditorRef.current.insertText(insertion);
      }
    }
  };

  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant='ghost'
            size='sm'
            mb={4}
            onClick={() => history.push('/admin/management/email-template')}
          >
            Back to List
          </Button>
          <Text
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            Email Template Detail
          </Text>
        </Flex>
      </CardHeader>
      <CardBody pb={0}>
        {/* Part 1: Template Info */}
        <Card mb={6}>
          <CardHeader>
            <Text fontSize='lg' fontWeight='bold' color={textColor}>
              Information
            </Text>
          </CardHeader>
          <CardBody>
            <form id='template-info-form' onSubmit={handleSubmitTemplate(onSubmitTemplate)}>
              <Flex direction='row' gap={6} flexWrap='wrap' alignItems='flex-start'>
                <Box flex='0 0 auto'>
                  <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                    Code
                  </FormLabel>
                  <Input
                    fontSize='sm'
                    type='text'
                    value={templateData?.code || ''}
                    size='md'
                    isDisabled
                    bg='gray.50'
                    width='200px'
                  />
                </Box>
                <Box flex='0 0 auto'>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                      Name
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      type='text'
                      placeholder='Name'
                      maxLength={250}
                      size='md'
                      width='280px'
                      name='name'
                      {...registerTemplate('name')}
                      isInvalid={errorsTemplate.name}
                      errorBorderColor='red.300'
                    />
                    {errorsTemplate.name?.message && (
                      <Text color='red' fontSize={14} mt='5px'>
                        {errorsTemplate.name?.message}
                      </Text>
                    )}
                  </FormControl>
                </Box>
                <Box flex='1'>
                  <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                    Keys
                  </FormLabel>
                  <Box
                    border='1px solid'
                    borderColor='gray.200'
                    borderRadius='md'
                    p={3}
                    minH='42px'
                  >
                    {paramTags.length > 0 && (
                      <Wrap spacing={2} mb={3}>
                        {paramTags.map((tag) => (
                          <WrapItem key={tag}>
                            <Tag size='sm' colorScheme='blue' borderRadius='full'>
                              <TagLabel>{tag}</TagLabel>
                              <TagCloseButton onClick={() => handleRemoveParam(tag)} />
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                    <Flex gap={2} maxW='350px'>
                      <Input
                        fontSize='sm'
                        type='text'
                        placeholder='Enter key and press Enter or comma'
                        size='sm'
                        value={paramInput}
                        onChange={(e) => setParamInput(e.target.value)}
                        onKeyDown={handleParamInputKeyDown}
                      />
                      <Button size='sm' onClick={handleAddParam} colorScheme='blue' variant='outline'>
                        Add
                      </Button>
                    </Flex>
                  </Box>
                </Box>
              </Flex>
              <Flex alignItems='center' gap={4} mt={4}>
                <Switch
                  name='isActive'
                  isChecked={watchTemplate('isActive')}
                  onChange={(e) => setValueTemplate('isActive', e.target.checked)}
                  size='sm'
                  colorScheme='blue'
                >
                  {watchTemplate('isActive') ? 'Active' : 'InActive'}
                </Switch>
                <Button
                  colorScheme='blue'
                  type='submit'
                  isLoading={isSubmittingTemplate}
                  size='sm'
                >
                  Save
                </Button>
              </Flex>
            </form>
          </CardBody>
        </Card>

        {/* Part 2: Template Details */}
        <Card pb={0} mb={0}>
          <CardHeader pb={0}>
            <Flex justifyContent='space-between' alignItems='center' width='100%'>
              <Text fontSize='lg' fontWeight='bold' color={textColor}>
                Template Details
              </Text>
              {checkForbidden(133) && (
                <Button
                  size='sm'
                  onClick={handleOpenCreateDetail}
                  leftIcon={<AddIcon />}
                  colorScheme='blue'
                  variant='outline'
                >
                  Add Template Detail
                </Button>
              )}
            </Flex>
          </CardHeader>
          <CardBody pt={2} pb={0}>
            <SearchTable2
              tableData={templateDetails}
              columnsData={columnsDataEmailTemplateDetail}
              totalRow={templateDetails.length}
              edit={editDetail}
              copy={copyDetail}
              listPermissions={[134]}
              actionsActive={['edit', 'copy']}
              loadData={() => {}}
              isFetching={isFetching}
              manualPagination={false}
              enablePagination={false}
            />
          </CardBody>
        </Card>

        {/* Modal for Template Detail */}
        <Modal
          closeOnOverlayClick={false}
          isOpen={isOpen}
          onClose={() => {
            onClose();
            resetDetail();
          }}
          size='6xl'
        >
          <ModalOverlay />
          <form onSubmit={handleSubmitDetail(onSubmitDetail)}>
            <ModalContent maxW='95vw' w='95vw'>
              <ModalHeader>
                {isCreateDetail ? 'Add New Template Detail' : 'Edit Template Detail'}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <FormControl isRequired mb={4}>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                    Type
                  </FormLabel>
                  <Select
                    fontSize='sm'
                    ms='4px'
                    size='md'
                    name='type'
                    {...registerDetail('type')}
                    isDisabled={!isCreateDetail}
                  >
                    {TYPE_OPTIONS.map((item, index) => (
                      <option key={index} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired mb={4}>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                    Name
                  </FormLabel>
                  <Input
                    fontSize='sm'
                    ms='4px'
                    type='text'
                    placeholder='Name'
                    maxLength={250}
                    size='md'
                    name='detailName'
                    {...registerDetail('detailName')}
                    isInvalid={errorsDetail.detailName}
                    errorBorderColor='red.300'
                  />
                  {errorsDetail.detailName?.message && (
                    <Text color='red' fontSize={14} mt='5px'>
                      {errorsDetail.detailName?.message}
                    </Text>
                  )}
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                    Subject
                  </FormLabel>
                  <Input
                    fontSize='sm'
                    ms='4px'
                    type='text'
                    placeholder='Subject'
                    maxLength={500}
                    size='md'
                    name='subject'
                    onChange={(e) => {
                      registerDetail('subject').onChange(e);
                      handleSubjectChange(e);
                    }}
                    onBlur={registerDetail('subject').onBlur}
                    onFocus={() => { lastFocusedField.current = 'subject'; }}
                    ref={(e) => {
                      registerDetail('subject').ref(e);
                      subjectInputRef.current = e;
                    }}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                    Email From
                  </FormLabel>
                  <Input
                    fontSize='sm'
                    ms='4px'
                    type='text'
                    placeholder='Email From'
                    maxLength={250}
                    size='md'
                    name='email_from'
                    {...registerDetail('email_from')}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                    Body
                  </FormLabel>
                  <Editor
                    ref={bodyEditorRef}
                    name='body'
                    control={controlDetail}
                    defaultValue=''
                    onFocus={() => { lastFocusedField.current = 'body'; }}
                    onSendTest={handleOpenSendTest}
                  />
                </FormControl>
                {templateKeys.length > 0 && (
                  <TemplateKeyInsert keys={templateKeys} onInsert={insertTemplateKey} />
                )}
                <FormControl>
                  <Switch
                    id='isActiveDetail'
                    name='isActiveDetail'
                    {...registerDetail('isActiveDetail')}
                    size='sm'
                    ms='4px'
                  >
                    Active
                  </Switch>
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme='blue'
                  mr={3}
                  type='submit'
                  isLoading={isSubmittingDetail}
                >
                  {isCreateDetail ? 'Create' : 'Update'}
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    resetDetail();
                  }}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          </form>
        </Modal>
        {/* Modal for Send Test Email */}
        <Modal
          closeOnOverlayClick={false}
          isOpen={isOpenSendTest}
          onClose={onCloseSendTest}
          size='xl'
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Send Test Email</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <FormControl isRequired mb={4}>
                <FormLabel fontSize='sm' fontWeight='normal'>
                  To Email
                </FormLabel>
                <Input
                  fontSize='sm'
                  type='email'
                  placeholder='email@example.com'
                  size='md'
                  value={sendTestEmail}
                  onChange={(e) => setSendTestEmail(e.target.value)}
                />
              </FormControl>
              <FormControl mb={4}>
                <FormLabel fontSize='sm' fontWeight='normal'>
                  Template ID
                </FormLabel>
                <Input
                  fontSize='sm'
                  size='md'
                  value={templateData?.code || ''}
                  isDisabled
                  bg='gray.50'
                />
              </FormControl>
              {paramTags.length > 0 && (
                <Box mb={4}>
                  <FormLabel fontSize='sm' fontWeight='normal'>
                    Params
                  </FormLabel>
                  <Stack spacing={3}>
                    {paramTags.map((key) => (
                      <FormControl key={key}>
                        <FormLabel fontSize='xs' color='gray.500' mb={1}>
                          {key}
                        </FormLabel>
                        <Input
                          fontSize='sm'
                          size='sm'
                          placeholder={key}
                          value={sendTestParams[key] || ''}
                          onChange={(e) =>
                            setSendTestParams((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                        />
                      </FormControl>
                    ))}
                  </Stack>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme='blue'
                mr={3}
                onClick={handleSendTestEmail}
                isLoading={isSendingTest}
                leftIcon={<EmailIcon />}
              >
                Send
              </Button>
              <Button onClick={onCloseSendTest}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
}

export default DataTableEmailTemplateDetail;
